/**
 * ⑭ R14a — 탐색 v2 구조의 단일 출처 (NAV14_20260929 「R14a 탐색」).
 *
 * RD(2026-10-11, 화면 예산제 §3-1) — 탭 5 = 홈·부동산·블로그·게시판·주식. 모바일 하단 탭과 데스크톱 상단이
 * «이 배열 하나» 를 읽는다(데스크톱 = 홈을 뺀 4). 하위 링크 합계 ≤ 12(G1), 더보기 없음.
 * 두 벌로 적으면 한쪽만 바뀐다(옛 Navigation 의 NAV_ITEMS/MOBILE_TABS 가 그렇게 갈렸다).
 * /more 라우트와 MORE_GROUPS 는 «메뉴에서만» 빠졌다 — URL 삭제 0, 페이지는 그대로 산다.
 *
 * ⛔ 새 경로를 만들지 않는다. 모든 href 는 이미 있는 라우트다(테스트가 app 디렉터리와 대조한다).
 * ⛔ 라벨 3종은 APT_LABEL 한 곳 — 메뉴·탭·칩·빵부스러기가 같은 말을 쓴다.
 * ⛔ 이 파일은 순수 데이터다(훅·DB·브라우저 API 없음). 서버·클라이언트 어디서나 import 가능.
 */
import { KAKAO_TALK_URL } from '@/lib/talk-banner';
import { CAFE_BASE_URL } from '@/lib/cafe/contract';
import { isHomePath } from '@/lib/route/is-home';

/** 라벨 3종 통일(⑭ 규칙) — 메뉴·탭·칩·빵부스러기·title 꼬리가 같은 말을 쓴다. */
export const APT_LABEL = {
  /** L6 tabs-r1 — /apt 허브 한 단어(빵부스러기 「홈 › 분양 › 미분양」 · 탭 「분양」 · 상단 「분양」). 종전 빵부스러기 「부동산」. */
  hub: '분양',
  subscription: '청약·분양예정',
  redev: '재개발·재건축',
  unsold: '미분양',
} as const;

/* ───────────── L6 tabs-r1 — 라벨 한 벌(v2) ─────────────
 * 모바일 하단 탭·데스크톱 상단 메뉴·레거시 Navigation 이 «같은 낱말» 을 쓴다: 분양 · 청약 일정 · 재개발 · 분석 글 · 지역.
 *   종전(v1): 하단 탭 「청약」·「블로그」 / 상단 「재개발·재건축」 — 같은 곳을 다른 말로 불렀다.
 * 스위치 app_config nav/labels_v2(기본 true) — OFF 면 v1 낱말로 돌아간다(구성·순서·href 는 그대로). 읽기는 lib/nav/nav-labels-flag.ts.
 *   ⚠️ NavV2 는 클라이언트 트리(ClientShell) 안이라 DB 를 읽지 못한다 → 서버에서 읽은 값을 prop(labelsV2)으로 받는다. 기본값 = NAV_LABELS_V2_DEFAULT. */
export const NAV_LABELS_V2_DEFAULT = true;

export type NavLabelKey = 'home' | 'apt' | 'calendar' | 'redev' | 'region' | 'blog' | 'tools' | 'more' | 'stock' | 'board';

/** 라벨 표 — v2(한 벌 · 모든 면 같은 말) · v1(종전 — 하단 탭 tab 과 상단 메뉴 top 이 달랐다). 종전 레거시 Navigation 은 tab 쪽을 따른다. */
const NAV_LABEL_TABLE: Record<NavLabelKey, { v2: string; v1: { tab: string; top: string } }> = {
  home: { v2: '홈', v1: { tab: '홈', top: '홈' } },
  apt: { v2: APT_LABEL.hub, v1: { tab: '분양', top: '분양' } },
  calendar: { v2: '청약 일정', v1: { tab: '청약', top: '청약 일정' } },
  redev: { v2: '재개발', v1: { tab: '재개발', top: APT_LABEL.redev } },
  region: { v2: '지역', v1: { tab: '지역', top: '지역' } },
  blog: { v2: '분석 글', v1: { tab: '블로그', top: '분석 글' } },
  tools: { v2: '도구', v1: { tab: '도구', top: '도구' } },
  more: { v2: '더보기', v1: { tab: '더보기', top: '더보기' } },
  stock: { v2: '주식', v1: { tab: '주식', top: '주식' } },
  board: { v2: '게시판', v1: { tab: '게시판', top: '게시판' } },
};

export type NavLabelSurface = 'tab' | 'top';

/** 탭·메뉴 라벨 한 곳. v2=false 면 종전 낱말(면별). */
export function navLabel(key: NavLabelKey, v2: boolean = NAV_LABELS_V2_DEFAULT, surface: NavLabelSurface = 'tab'): string {
  const t = NAV_LABEL_TABLE[key];
  return v2 ? t.v2 : t.v1[surface];
}

/** 라벨 플래그 값 해석 — 없음·null = 켬(기본). false·'false'·'0'·'off'·{enabled:false} = 끔. */
export function parseNavLabelsV2(v: unknown): boolean {
  if (v === undefined || v === null) return NAV_LABELS_V2_DEFAULT;
  if (v === false || v === 0) return false;
  if (typeof v === 'string') return !['false', '0', 'off', 'no'].includes(v.trim().toLowerCase());
  if (typeof v === 'object' && !Array.isArray(v) && 'enabled' in (v as Record<string, unknown>)) return parseNavLabelsV2((v as Record<string, unknown>).enabled);
  return true;
}

/** L6 tabs-r1 — 모바일 하단 탭 6 = 홈 · 분양 · 청약 일정 · 재개발 · 분석 글 · 더보기(구성·순서 현행 그대로 · 라벨만 한 벌). NavV2 DS2_TABS 가 여기로 이관됐다. */
export type NavTabKey = 'home' | 'apt' | 'calendar' | 'redev' | 'blog' | 'more' | 'region';

/** CL — 데스크톱 상단 메뉴 6 = 분양 · 청약 일정 · 재개발·재건축 · 지역 · 분석 글 · 도구(+ 검색 자리). */
export type TopMenuKey = 'apt' | 'calendar' | 'redev' | 'region' | 'blog' | 'tools';

export interface NavLink {
  /** 계측 id(짧은 영문). nav_click·more_click 의 event_name. */
  id: string;
  label: string;
  href: string;
  /** /more·드롭다운의 한 줄 보조 설명. */
  sub?: string;
  /** 외부 링크(카톡방). */
  external?: boolean;
  /** 색인 제외 화면(광고 신청·내 광고·광고 약관)으로 가는 링크 — rel="nofollow". */
  nofollow?: boolean;
  /**
   * retention-1031 — 수화 뒤 지역 칩(GlobalRegionChip)이 현재 지역으로 갈아끼우는 링크 종류(data-kd-region-link).
   *   서버 HTML 은 href 그대로(방문자 무관 · 같은 URL = 같은 HTML).
   */
  regionLink?: 'lead';
}

export interface NavTab {
  key: NavTabKey;
  /** 라벨 키(navLabel) — 화면이 플래그로 v1/v2 를 고른다. label 은 v2 기본값(테스트·감사용). */
  labelKey: NavLabelKey;
  label: string;
  href: string;
  /** 2층(데스크톱 드롭다운). 없으면 드롭다운 없이 링크만. */
  children?: readonly NavLink[];
  /** 외부 링크(카페) — 새 창. */
  external?: boolean;
}

export interface TopMenuItem {
  key: TopMenuKey;
  label: string;
  href: string;
}

/** 청약 캘린더 — 전용 라우트가 없다(/apt/big-events 는 재건축 «이벤트» 목록·noindex). /apt 의 「지금 청약」 절(#now)로 보낸다. */
export const APT_CALENDAR_HREF = '/apt#now';

/** 관심고객 등록 — 독립 라우트가 없다. 지역 허브 리드폼(부산 · 부울경 우선) 앵커로 보낸다.
 *  ⚠️ 이 값은 «서버 HTML 기본값» 이다. 수화 뒤에는 현재 지역(경로 > 상세 현장 지역 > 기억)의 허브 폼으로 바뀐다
 *     (regionLink 'lead' · region-scope leadRegisterHrefFor · retention-1031). 지역을 모르면 이 값 그대로. */
export const LEAD_REGISTER_HREF = `/apt/region/${encodeURIComponent('부산')}#lead-form`;

/** 메뉴에서 빠진 링크 — /more 가 «하나도 지우지 않고» 계속 받는다(테스트가 보존을 잠근다).
 *  L6 tabs-r1 — MORE_GROUPS 에 같은 href 로 이미 있는 4건(가점 진단 · 부동산 통계 · 보도자료 · 테마주)은 뺐다 — 한 링크 한 자리. */
export const MENU_DROPPED: readonly NavLink[] = [
  { id: 'apt_calendar', label: '청약 캘린더', href: APT_CALENDAR_HREF },
  { id: 'board_ask', label: '질문 올리기', href: '/board/write?cat=qa' },
  { id: 'stock_compare', label: '종목 비교', href: '/stock/compare' },
];

export interface MoreGroup {
  key: MoreGroupKey;
  title: string;
  /** 묶음마다 1줄 설명(⑭ 규칙). */
  desc: string;
  items: readonly NavLink[];
  /** 설정 묶음의 「글자 크기」처럼 링크가 아닌 조작. 화면이 그린다(/more 만 — 헤더 더보기 패널은 링크만). */
  controls?: readonly ('font-size')[];
}

export type MoreGroupKey = 'ad' | 'presale' | 'region' | 'community' | 'stock' | 'me' | 'help';

/** 광고 묶음 — 광고주 진입. 출발 지면 표지 from=more(광고 페이지·신청서가 출처로 읽는다). */
export const MORE_AD_FROM = 'more';

/**
 * more-tidy — 더보기 7묶음(모바일 /more · PC 헤더 「더보기」 패널이 같은 표를 읽는다).
 *   광고(맨 위 · 광고주 모집) → 분양·청약 → 지역·시세 → 글·커뮤니티 → 주식 → 내 정보·설정 → 고객 안내.
 *
 * 걷어낸 항목(라우트·리다이렉트는 그대로 — 메뉴에서만 뺐다):
 *   /stock/dividend · /stock/compare → 301 /stock(종목 항목과 중복) · /glossary → 301 /blog · /daily → 301 /apt/region(지역 항목과 중복)
 *   · /discuss → 301 /blog · /consultant(「준비 중」 빈 화면 · noindex) · /search(헤더 검색과 중복).
 * ⛔ /blog 는 넣지 않는다(하단 탭 「블로그」와 중복 — nav-live 테스트). 카페 링크는 넣지 않는다(헤더 HTML 에 cafe 0 — nav 테스트).
 */
export const MORE_GROUPS: readonly MoreGroup[] = [
  {
    key: 'ad',
    title: '광고',
    desc: '분양 현장·지역 페이지에 광고를 싣고 관리합니다.',
    items: [
      { id: 'ad_info', label: '광고하기', href: `/advertise?from=${MORE_AD_FROM}`, sub: '상품·지면 안내' },
      { id: 'ad_buy', label: '광고 신청', href: `/ads/buy?from=${MORE_AD_FROM}`, sub: '바로 신청·결제', nofollow: true },
      { id: 'ad_quote', label: '견적서 받기', href: `/advertise?from=${MORE_AD_FROM}#quote`, sub: '조건별 견적 요청' },
      { id: 'ad_my', label: '내 광고', href: '/ads/my', sub: '상태·소재·연장', nofollow: true },
      { id: 'ad_terms', label: '광고 약관', href: '/ads/terms', sub: '약관·환불 규정', nofollow: true },
    ],
  },
  {
    key: 'presale',
    title: '분양·청약',
    desc: '청약 일정과 분양예정·정비사업 현장을 봅니다.',
    items: [
      { id: 'apt_subscription', label: APT_LABEL.subscription, href: '/apt', sub: '지금 청약 · 곧 나올 현장' },
      { id: 'apt_calendar', label: '청약 일정', href: '/calendar', sub: '접수·발표 달력' },
      { id: 'tool_redev', label: APT_LABEL.redev, href: '/apt/redev', sub: '정비사업 현황' },
      { id: 'apt_unsold', label: APT_LABEL.unsold, href: '/apt/unsold', sub: '시도별 미분양 현황' },
      { id: 'tool_diagnose', label: '가점 진단', href: '/apt/diagnose', sub: '청약 가점 계산' },
      { id: 'join_lead', label: '관심고객 등록', href: LEAD_REGISTER_HREF, sub: '분양 소식 먼저 받기', regionLink: 'lead' },
    ],
  },
  {
    key: 'region',
    title: '지역·시세',
    desc: '지역별 분양과 단지 시세·실거래를 찾습니다.',
    items: [
      { id: 'apt_region', label: '지역별 분양', href: '/apt/region', sub: '시도·시군구 허브' },
      { id: 'tool_complex', label: '단지백과', href: '/apt/complex', sub: '전국 단지 시세' },
      { id: 'tool_apt_search', label: '실거래 검색', href: '/apt/search', sub: '전국 실거래가' },
      { id: 'tool_apt_data', label: '부동산 통계', href: '/apt/data', sub: '공공 데이터' },
      { id: 'tool_calc', label: '계산기', href: '/calc', sub: '부동산·세금 계산' },
    ],
  },
  {
    key: 'community',
    title: '글·커뮤니티',
    desc: '연재 글을 읽고 이야기를 나눕니다.',
    items: [
      { id: 'tool_series', label: '시리즈', href: '/blog/series', sub: '주제별 연재 모음' },
      { id: 'join_board', label: '게시판', href: '/board', sub: '현장 토론방 · 질문답변 · 자유글' },
      { id: 'join_talk', label: '카톡방', href: KAKAO_TALK_URL, sub: '부정공 단톡방 입장', external: true },
    ],
  },
  {
    key: 'stock',
    title: '주식',
    desc: '종목 시세와 테마·등락을 봅니다.',
    items: [
      { id: 'stock_home', label: '종목 시세', href: '/stock', sub: '국내·해외 시세' },
      { id: 'stock_themes', label: '테마주', href: '/stock/themes', sub: '섹터별 관련주' },
      { id: 'stock_movers', label: '급등락', href: '/stock/movers', sub: '등락률 상위' },
      { id: 'stock_search', label: '종목 검색', href: '/stock/search', sub: '지표로 걸러보기' },
      { id: 'stock_data', label: '주식 통계', href: '/stock/data', sub: '시장 데이터' },
    ],
  },
  {
    key: 'me',
    title: '내 정보·설정',
    desc: '지역·알림·글자 크기를 정합니다.',
    controls: ['font-size'],
    items: [
      { id: 'set_region', label: '지역 설정', href: '/settings/region', sub: '우리동네 설정·변경' },
      { id: 'set_notify', label: '알림 설정', href: '/notifications/settings', sub: '푸시·이메일·카카오' },
      { id: 'set_interests', label: '관심사 설정', href: '/settings/interests', sub: '맞춤 알림 주제' },
    ],
  },
  {
    key: 'help',
    title: '고객 안내',
    desc: '이용 방법과 약관·개인정보 처리 기준을 안내합니다.',
    items: [
      { id: 'set_guide', label: '가이드북', href: '/guide', sub: '이용 방법 안내' },
      { id: 'set_install', label: '앱 설치', href: '/guide#install', sub: '홈 화면에 추가' },
      { id: 'set_faq', label: 'FAQ', href: '/faq', sub: '자주 묻는 질문' },
      { id: 'set_about', label: '서비스 소개', href: '/about', sub: '카더라 소개' },
      { id: 'tool_press', label: '보도자료', href: '/press', sub: '미디어 키트' },
      { id: 'help_terms', label: '이용약관', href: '/terms', sub: '서비스 이용 조건' },
      { id: 'help_privacy', label: '개인정보처리방침', href: '/privacy', sub: '개인정보 처리 기준' },
    ],
  },
];

/** 네이버 카페(부정공) — 본체 밖 «공식 카페» 링크. ⛔ Hostinger 위성 링크 아님. */
/** hc3 H6 — 카페 주소 단일 출처(cafe/contract CAFE_BASE_URL, 값 불변). */
export const NAV_CAFE_URL = CAFE_BASE_URL;

/** 도구 착지 — 레인 C 「도구」 착지 페이지가 정해지면 그 경로로 바꾼다(기본값 /calc · 실재 라우트). */
export const TOOLS_HREF = '/calc';

/** L6 tabs-r1 — 모바일 하단 탭 6(홈 · 분양 · 청약 일정 · 재개발 · 분석 글 · 더보기). 정적 a href. NavV2·테스트가 이 배열 하나를 읽는다. */
export const NAV_TABS: readonly NavTab[] = [
  { key: 'home', labelKey: 'home', label: navLabel('home'), href: '/' },
  { key: 'apt', labelKey: 'apt', label: navLabel('apt'), href: '/apt' },
  { key: 'calendar', labelKey: 'calendar', label: navLabel('calendar'), href: '/calendar' },
  { key: 'redev', labelKey: 'redev', label: navLabel('redev'), href: '/apt/redev' },
  { key: 'blog', labelKey: 'blog', label: navLabel('blog'), href: '/blog' },
  { key: 'more', labelKey: 'more', label: navLabel('more'), href: '/more' },
];

/* ───────────── M6 tabs — 하단 탭 재선정(v3 · 데이터 근거) ─────────────
 * 스위치 app_config nav/tabs_v3(기본 false — 운영자가 보고 켠다). 꺼져 있으면 위 NAV_TABS(6칸) 그대로.
 * 근거(최근 30일 page_views bot_type='human' · user_events tab_click):
 *   /apt/region·/apt/area 2,614회(1,392명) · /blog 2,347회(1,631명) — 탭 클릭 홈 74 · 더보기 47.
 *   /calendar 133회(83명) · /apt/redev 343회(128명) — 둘은 「분양」 허브·더보기에서 계속 닿는다(탭에서만 뺀다 · 라우트 그대로).
 * 읽기는 lib/nav/nav-tabs-flag.ts. NavV2 는 서버가 읽은 값을 prop(tabsV3)으로 받는다. */
export const NAV_TABS_V3_DEFAULT = false;

/** M6 — 하단 탭 5(홈 · 분양 · 지역 · 분석 글 · 더보기). 전부 실재 라우트. */
export const NAV_TABS_V3: readonly NavTab[] = [
  { key: 'home', labelKey: 'home', label: navLabel('home'), href: '/' },
  { key: 'apt', labelKey: 'apt', label: navLabel('apt'), href: '/apt' },
  { key: 'region', labelKey: 'region', label: navLabel('region'), href: '/apt/region' },
  { key: 'blog', labelKey: 'blog', label: navLabel('blog'), href: '/blog' },
  { key: 'more', labelKey: 'more', label: navLabel('more'), href: '/more' },
];

/** tabs_v3 플래그 값 해석 — 없음·null = 끔(기본). true·'true'·'1'·'on'·{enabled:true} = 켬. */
export function parseNavTabsV3(v: unknown): boolean {
  if (v === undefined || v === null) return NAV_TABS_V3_DEFAULT;
  if (v === true || v === 1) return true;
  if (typeof v === 'string') return ['true', '1', 'on', 'yes'].includes(v.trim().toLowerCase());
  if (typeof v === 'object' && !Array.isArray(v) && 'enabled' in (v as Record<string, unknown>)) return parseNavTabsV3((v as Record<string, unknown>).enabled);
  return false;
}

/** 하단 탭 라벨을 플래그로 고른 사본(v2=false → 종전 「청약」·「블로그」). M6 — tabsV3=true 면 v3 구성(5칸). */
export function navTabsOf(labelsV2: boolean, tabsV3: boolean = NAV_TABS_V3_DEFAULT): NavTab[] {
  return (tabsV3 ? NAV_TABS_V3 : NAV_TABS).map((t) => ({ ...t, label: navLabel(t.labelKey, labelsV2) }));
}

/** CL §4-4 — 데스크톱 상단 메뉴 6(드롭다운 없음 · 사이트링크 대비 정적 a href). 주식·피드·토론 메뉴 없음. L6 — 라벨은 navLabel 한 벌. */
export const TOP_MENU: readonly TopMenuItem[] = [
  { key: 'apt', label: navLabel('apt'), href: '/apt' },
  { key: 'calendar', label: navLabel('calendar'), href: '/calendar' },
  { key: 'redev', label: navLabel('redev'), href: '/apt/redev' },
  { key: 'region', label: navLabel('region'), href: '/apt/region' },
  { key: 'blog', label: navLabel('blog'), href: '/blog' },
  { key: 'tools', label: navLabel('tools'), href: TOOLS_HREF },
];

/** 상단 메뉴 라벨을 플래그로 고른 사본. */
export function topMenuOf(labelsV2: boolean): TopMenuItem[] {
  return TOP_MENU.map((m) => ({ ...m, label: navLabel(m.key, labelsV2, 'top') }));
}

/** 레거시 Navigation(킬 스위치 ui/nav_v2=false) 상단·하단 — 아이콘은 Navigation 이 href 로 붙인다. 라벨은 같은 표(navLabel). */
export const LEGACY_NAV_ITEMS: ReadonlyArray<{ key: NavLabelKey; href: string; mobile: boolean }> = [
  { key: 'home', href: '/', mobile: true },
  { key: 'stock', href: '/stock', mobile: true },
  { key: 'apt', href: '/apt', mobile: true },
  { key: 'blog', href: '/blog', mobile: true },
  { key: 'board', href: '/board', mobile: false },
];

const under = (p: string, base: string) => p === base || p.startsWith(`${base}/`);
const isRegionPath = (p: string) => under(p, '/apt/region') || under(p, '/apt/area');

/** /more 바로가기 타일 8(L6) — 게시판 · 미분양 · 계산기 · 단지백과 · 주식 · 지역별 분양 · 알림 · 지역 설정. 전부 MORE_GROUPS 의 항목(id 참조 · 중복 정의 0). */
export const MORE_QUICK_IDS: readonly string[] = ['join_board', 'apt_unsold', 'tool_calc', 'tool_complex', 'stock_home', 'apt_region', 'set_notify', 'set_region'];
export function moreQuickLinks(): NavLink[] {
  const all = new Map<string, NavLink>();
  for (const g of MORE_GROUPS) for (const i of g.items) all.set(i.id, i);
  return MORE_QUICK_IDS.flatMap((id) => { const l = all.get(id); return l ? [l] : []; });
}

/** 경로 → 활성 하단 탭. 방문자와 무관(경로만). 소속 탭이 없으면 null. L6 — NavV2 ds2TabOf 와 한 함수(ds2TabOf 는 별칭). */
export function activeTabOf(pathname: string | null | undefined, tabsV3: boolean = NAV_TABS_V3_DEFAULT): NavTabKey | null {
  const p = pathname || '';
  if (isHomePath(p)) return 'home';
  if (tabsV3) {
    // M6 — v3 엔 청약 일정·재개발 탭이 없다 → 분양 허브 소속으로 본다. 지역 허브는 「지역」.
    if (isRegionPath(p)) return 'region';
    if (under(p, '/apt') || under(p, '/calendar')) return 'apt';
    if (under(p, '/blog')) return 'blog';
    if (under(p, '/more')) return 'more';
    return null;
  }
  if (under(p, '/calendar')) return 'calendar';
  if (under(p, '/apt/redev')) return 'redev';
  if (under(p, '/apt')) return 'apt';
  if (under(p, '/blog')) return 'blog';
  if (under(p, '/more')) return 'more';
  return null;
}

/** 경로 → 활성 상단 메뉴. */
export function activeTopOf(pathname: string | null | undefined): TopMenuKey | null {
  const p = pathname || '';
  if (under(p, '/calendar')) return 'calendar';
  if (under(p, '/apt/redev')) return 'redev';
  if (isRegionPath(p)) return 'region';
  if (under(p, '/apt')) return 'apt';
  if (under(p, '/blog')) return 'blog';
  if (under(p, TOOLS_HREF)) return 'tools';
  return null;
}

/** 탭·드롭다운 전체 링크(테스트·감사용). */
export function allNavHrefs(): string[] {
  const out = new Set<string>();
  for (const t of [...NAV_TABS, ...NAV_TABS_V3]) {
    out.add(t.href);
    for (const c of t.children ?? []) out.add(c.href);
  }
  for (const m of TOP_MENU) out.add(m.href);
  for (const c of MENU_DROPPED) out.add(c.href);
  for (const g of MORE_GROUPS) for (const i of g.items) out.add(i.href);
  return [...out];
}
